import { useEffect, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getPreciseLocation } from '@/lib/geolocation';

const getOrCreateSessionId = (): string => {
  const key = 'draw_session_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
};

export type DrawPresencePayload = {
  sessionId: string;
  min: number;
  max: number;
  count: number;
  started_at: string;
  path: string;
  referrer: string;
  user_agent: string;
  platform: string;
  street_address?: string;
  city?: string;
  country?: string;
  region?: string;
};

export const useDrawPresence = () => {
  const sessionId = useMemo(() => getOrCreateSessionId(), []);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    const channel = supabase.channel('draws', {
      config: { presence: { key: sessionId } },
    });
    channelRef.current = channel;
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        // no-op; we'll call track() on demand
      }
    });

    return () => {
      channel.unsubscribe();
    };
  }, [sessionId]);

  const registerDraw = async (min: number, max: number, count: number) => {
    // Detect platform
    const ua = navigator.userAgent;
    let platform = 'web';
    if (/android/i.test(ua)) platform = 'android';
    else if (/iPad|iPhone|iPod/.test(ua)) platform = 'ios';
    else if (/Windows/.test(ua)) platform = 'windows';
    else if (/Macintosh/.test(ua)) platform = 'mac';
    else if (/Linux/.test(ua)) platform = 'linux';

    const locationData = await getPreciseLocation();

    const payload: DrawPresencePayload = {
      sessionId,
      min,
      max,
      count,
      started_at: new Date().toISOString(),
      path: window.location.pathname,
      referrer: document.referrer || 'direct',
      user_agent: navigator.userAgent,
      platform,
      street_address: locationData.streetAddress,
      city: locationData.city,
      country: locationData.country,
      region: locationData.region,
    };

    try {
      await channelRef.current?.track(payload);
      
      // Persist session to history
      const { data, error } = await supabase.functions.invoke('track-session', {
        body: payload,
      });
      
      if (error) {
        console.error('Failed to save session:', error);
      } else {
        console.log('✅ Session saved successfully:', data);
      }
    } catch (e) {
      console.error('Session tracking error:', e);
    }
  };

  return { sessionId, registerDraw };
};
